import { Backend } from './Http/Backend';
import { HttpBackend } from './Http/HttpBackend';

export type ServiceLifecycleImage = { url: string; alt: string };

export type ServiceLifecycle = {
  banner: {
    enabled: boolean;
    presentation?: 'BANNER' | 'CARD';
    image?: ServiceLifecycleImage | null;
    title?: Record<string, string>;
    highlightId?: string | null;
    expiresAt?: string | null;
    text: Record<string, string>;
    actionLabel: Record<string, string>;
    actionUrl: string | null;
  };
  shutdown: {
    enabled: boolean;
    gymEnabled?: boolean;
    gymIds?: string[];
    startsAt: string | null;
    title: Record<string, string>;
    text: Record<string, string>;
    actionLabel: Record<string, string>;
    actionUrl: string;
  };
  locked: boolean;
  serverTime: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((text) => typeof text === 'string');
}

function isHttpsUrl(value: unknown, aepsyOnly = false): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      (!aepsyOnly || url.hostname === 'aepsy.com' || url.hostname.endsWith('.aepsy.com'))
    );
  } catch {
    return false;
  }
}

function isLifecycleImage(value: unknown): value is ServiceLifecycleImage {
  return (
    isRecord(value) &&
    isHttpsUrl(value.url) &&
    value.url === value.url.trim() &&
    !value.url.includes('\\') &&
    typeof value.alt === 'string'
  );
}

export function isServiceLifecycle(value: unknown): value is ServiceLifecycle {
  if (!isRecord(value) || !isRecord(value.banner) || !isRecord(value.shutdown)) {
    return false;
  }
  const { banner, shutdown } = value;
  return (
    typeof value.locked === 'boolean' &&
    typeof value.serverTime === 'string' &&
    Number.isFinite(Date.parse(value.serverTime)) &&
    typeof banner.enabled === 'boolean' &&
    (banner.presentation === undefined || banner.presentation === 'BANNER' || banner.presentation === 'CARD') &&
    (banner.image == null || isLifecycleImage(banner.image)) &&
    isText(banner.text) &&
    isText(banner.actionLabel) &&
    (banner.title === undefined || isText(banner.title)) &&
    (banner.highlightId == null || typeof banner.highlightId === 'string') &&
    (banner.expiresAt == null ||
      (typeof banner.expiresAt === 'string' && Number.isFinite(Date.parse(banner.expiresAt)))) &&
    (banner.actionUrl === null || isHttpsUrl(banner.actionUrl)) &&
    typeof shutdown.enabled === 'boolean' &&
    (shutdown.gymEnabled === undefined || typeof shutdown.gymEnabled === 'boolean') &&
    (shutdown.gymIds === undefined ||
      (Array.isArray(shutdown.gymIds) && shutdown.gymIds.every((id) => typeof id === 'string'))) &&
    (shutdown.startsAt === null ||
      (typeof shutdown.startsAt === 'string' && Number.isFinite(Date.parse(shutdown.startsAt)))) &&
    isText(shutdown.title) &&
    isText(shutdown.text) &&
    isText(shutdown.actionLabel) &&
    isHttpsUrl(shutdown.actionUrl, true)
  );
}

export function lifecycleText(text: Record<string, string>, language: string): string {
  const locale = language.toLowerCase().replace('_', '-');
  return text[locale] || text[locale.split('-')[0]] || text.en || Object.values(text).find(Boolean) || '';
}

export function nextLifecycleRefresh(state: ServiceLifecycle): number {
  if (!state.banner.enabled || !state.banner.expiresAt) {
    return 30_000;
  }
  const untilExpiry = Date.parse(state.banner.expiresAt) - Date.parse(state.serverTime);
  return untilExpiry > 0 ? Math.max(1_000, Math.min(30_000, untilExpiry)) : 30_000;
}

const backend = new Backend(undefined, {}, '/');

function readLifecycle(value: unknown): ServiceLifecycle {
  if (
    isRecord(value) &&
    isRecord(value.banner) &&
    value.banner.image != null &&
    !isLifecycleImage(value.banner.image)
  ) {
    value = { ...value, banner: { ...value.banner, image: null } };
  }
  if (!isServiceLifecycle(value)) {
    throw new Error('Invalid service lifecycle response');
  }
  return value;
}

export const ServiceLifecycleApi = {
  async get(signal?: AbortSignal): Promise<ServiceLifecycle> {
    return readLifecycle(await backend.get('/service-lifecycle', undefined, { timeout: 5_000, signal }));
  },
  async getScoped(signal?: AbortSignal): Promise<ServiceLifecycle> {
    return readLifecycle(
      await HttpBackend.get('/coach-program/service-lifecycle', undefined, { timeout: 5_000, signal }),
    );
  },
};
